import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { LogIn, AlertCircle } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const AdminLogin = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      await login(formData.email, formData.password);
      navigate('/admin/dashboard');
    } catch (err) {
      setError(err.message || 'Login failed. Please check your credentials.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="admin-login-page" className="min-h-screen bg-[#FAF8F5] flex items-center justify-center px-6">
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="w-full max-w-md"
      >
        <div className="bg-white border border-[#E5E5E5] p-8">
          {/* Logo */}
          <div className="text-center mb-8">
            <h1 className="font-serif text-4xl font-bold tracking-tight text-[#C9A84C] mb-4" data-testid="admin-login-logo">
              De Azraa
            </h1>
            <h2 className="font-serif text-3xl tracking-tight font-light text-[#1A1A1A] mb-2">
              Admin Login
            </h2>
            <p className="text-sm text-[#3A3A3A]">Access the management dashboard</p>
          </div>

          {error && (
            <div data-testid="login-error" className="mb-6 p-4 bg-red-50 border border-red-200 text-red-800 flex items-start gap-2">
              <AlertCircle size={20} className="flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} data-testid="admin-login-form">
            <div className="space-y-6">
              <div>
                <label htmlFor="email" className="block text-xs tracking-[0.2em] uppercase font-semibold text-[#1A1A1A] mb-2">
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  data-testid="login-email-input"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full border border-[#E5E5E5] px-4 py-3 text-[#1A1A1A] focus:outline-none focus:border-[#C9A84C] transition-colors"
                />
              </div>

              <div>
                <label htmlFor="password" className="block text-xs tracking-[0.2em] uppercase font-semibold text-[#1A1A1A] mb-2">
                  Password
                </label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  data-testid="login-password-input"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  className="w-full border border-[#E5E5E5] px-4 py-3 text-[#1A1A1A] focus:outline-none focus:border-[#C9A84C] transition-colors"
                />
              </div>

              <button
                type="submit"
                data-testid="login-submit-btn"
                disabled={isSubmitting}
                className="w-full bg-[#1A1A1A] text-[#FAF8F5] hover:bg-[#C9A84C] hover:text-[#1A1A1A] transition-colors duration-300 px-8 py-4 text-sm tracking-widest uppercase disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {isSubmitting ? 'Logging in...' : 'Login'}
                <LogIn size={16} />
              </button>
            </div>
          </form>
        </div>

        <div className="mt-6 text-center">
          <a
            href="/"
            data-testid="back-to-home-link"
            className="text-sm text-[#3A3A3A] hover:text-[#C9A84C] transition-colors"
          >
            ← Back to Website
          </a>
        </div>
      </motion.div>
    </div>
  );
};

export default AdminLogin;